import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Clock, CheckCircle2, AlertCircle,
  BedDouble, Users, MapPin, CreditCard, Smartphone, Landmark,
  Wallet, Star,
} from "lucide-react";
import "./hotelconfirm.css";
import hotelApi, { initiateHotelEasebuzzPayment, verifyHotelEasebuzzPayment } from "../../../../services/hotelSearchApi";
import { useAuth } from "../../../../AuthProvider/AuthContext";

// ── Constants ────────────────────────────────────────────────────────────────
const API_BASE =
  (process.env.REACT_APP_API_URL || "https://makebeetrip.com").replace(/\/$/, "") + "/api";

const STORAGE_KEY = "makebeetrip_hotel_booking";

// ── Storage helpers (mirrors bus pattern exactly) ────────────────────────────
export function saveHotelBooking(booking) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(booking)); } catch { /* ignore */ }
}

export function readHotelBooking() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// ── Token helper (identical to bus) ─────────────────────────────────────────
const getStoredToken = () => {
  try {
    return (
      localStorage.getItem("mbt_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      ""
    );
  } catch { return ""; }
};

// ── Helpers ──────────────────────────────────────────────────────────────────
const money = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

const fmt = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function formatDate(d) {
  if (!d) return "—";
  try {
    const dt = new Date(`${d}T00:00:00`);
    if (!isNaN(dt))
      return dt.toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
      });
  } catch { /* ignore */ }
  return String(d);
}

function calcNights(checkIn, checkOut) {
  try {
    const diff = Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000);
    return diff > 0 ? diff : 1;
  } catch { return 1; }
}
async function downloadHotelReceipt(bookingId, token, ref) {
  const res = await fetch(
    `${API_BASE}/hotel-bookings/${bookingId}/receipt`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || "Download failed");
  }

  const blob = await res.blob();
  const objectUrl = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = `${ref || "hotel-receipt"}.pdf`;

  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(objectUrl);
}
// ── Easebuzz loader (identical to bus) ──────────────────────────────────────
const loadEasebuzzCheckout = () =>
  new Promise((resolve, reject) => {
    if (window.EasebuzzCheckout) { resolve(window.EasebuzzCheckout); return; }
    const existing = document.querySelector("script[data-easebuzz-checkout]");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.EasebuzzCheckout), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/easebuzz-checkout.js";
    script.async = true;
    script.dataset.easebuzzCheckout = "true";
    script.onload  = () => resolve(window.EasebuzzCheckout);
    script.onerror = () => reject(new Error("Could not load payment gateway. Please try again."));
    document.body.appendChild(script);
  });

// ── Countdown hook (identical to bus) ───────────────────────────────────────
function useCountdown(seconds) {
  const [remaining, setRemaining] = useState(seconds);
  useEffect(() => {
    if (remaining <= 0) return;
    const id = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(id);
  }, [remaining]);
  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");
  return { display: `${mm}:${ss}`, expired: remaining <= 0, remaining };
}

// ── Save confirmed hotel booking to DB ──────────────────────────────────────
async function saveConfirmedHotelBooking(booking, payment) {
  const token = getStoredToken();
  if (!token) throw new Error("Please log in to save this booking.");

  const nights = calcNights(booking.checkIn, booking.checkOut);
  const occupancy = booking.occupancies?.[0] || {};

  const res = await fetch(`${API_BASE}/hotel-bookings`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      // ── Hotel info ──
      hotelId:       booking.id        || booking.hotelId  || null,
      hotelName:     booking.name      || booking.hotelName,
      hotelLocation: booking.location  || null,
      hotelStars:    booking.stars     || booking.starRating || null,
      hotelImage:    booking.image     || null,

      // ── Stay ──
      checkIn:       booking.checkIn,
      checkOut:      booking.checkOut,
      nights,
      rooms:         booking.occupancies?.length || 1,
      adults:        occupancy.adultCount  || 2,
      roomType:      booking.selectedRoom?.name || booking.rateName || "Deluxe Room",

              // ── Pricing ──
        pricePerNight: Number(booking.price || 0),

        roomTotal: Number(
          booking.pricing?.supplier?.roomTotal ??
          booking.pricing?.supplierRoomTotal ??
          booking.baseRate ??
          booking.totalPrice ??
          0
        ),

        taxTotal: Number(
          booking.pricing?.taxTotal ??
          booking.taxes ??
          0
        ),

        grandTotal: Number(
          booking.pricing?.payableTotal ??
          booking.grandTotal ??
          booking.totalAmount ??
          booking.totalPrice ??
          0
        ),

      // ── Payment ──
      paymentMethod: payment.paymentMethod || "easebuzz",
      cardLast4:     payment.cardLast4     || null,
      cardName:      payment.cardName      || null,

      // ── Guest ──
      guestFirstName:  booking.guestFirstName  || booking.guest?.firstName  || "",
      guestLastName:   booking.guestLastName   || booking.guest?.lastName   || "",
      guestEmail:      booking.guestEmail      || booking.guest?.email      || "",
      guestPhone:      booking.guestPhone      || booking.guest?.phone      || "",
      guestAddress:    booking.guestAddress    || null,
      specialRequests: booking.specialRequests || null,
      ...(booking.gstDetails?.enabled ? { gstDetails: booking.gstDetails } : {}),
    }),
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok && res.status !== 409) {
    throw new Error(body.error || "Could not save hotel booking.");
  }
  return body;
}

// ── Success Popup ────────────────────────────────────────────────────────────
function SuccessPopup({ booking, txnid, dbBooking, token, onClose }) {
  const nights = calcNights(booking.checkIn, booking.checkOut);

  return (
    <div className="hc-overlay" role="dialog" aria-modal="true" aria-label="Booking confirmed">
      <div className="hc-popup">
        <div className="hc-popup-icon-wrap">
          <svg className="hc-checkmark" viewBox="0 0 52 52">
            <circle className="hc-checkmark-circle" cx="26" cy="26" r="25" fill="none" />
            <path   className="hc-checkmark-check"  fill="none" d="M14 27l8 8 16-16" />
          </svg>
        </div>

        <h2 className="hc-popup-title">Booking Confirmed!</h2>
        <p className="hc-popup-sub">
          Your hotel room is booked. Confirmation sent to{" "}
          <strong>{booking.guestEmail || booking.guest?.email || "your email"}</strong>.
        </p>

        <div className="hc-popup-ref">
          <span>Booking Reference</span>
          <strong>{dbBooking?.bookingReference || dbBooking?.bookingRefNo || txnid || "—"}</strong>
        </div>

        <div className="hc-popup-summary">
          <div className="hc-popup-row">
            <BedDouble size={14} />
            <span>{booking.name || booking.hotelName}</span>
          </div>
          <div className="hc-popup-row">
            <MapPin size={14} />
            <span>{booking.location || "—"}</span>
          </div>
          <div className="hc-popup-row">
            <Clock size={14} />
            <span>
              {formatDate(booking.checkIn)} → {formatDate(booking.checkOut)}
              {" "}({nights} night{nights !== 1 ? "s" : ""})
            </span>
          </div>
          <div className="hc-popup-row hc-popup-row--price">
            <CreditCard size={14} />
            <span>Amount Paid: <strong>{money(booking.grandTotal || booking.totalAmount || booking.totalPrice)}</strong></span>
          </div>
        </div>

        <div className="hc-popup-actions">
          <button className="hc-popup-btn hc-popup-btn--primary" onClick={onClose}>
            View My Bookings
          </button>
        {dbBooking?.id && (
  <button
    type="button"
    className="hc-popup-btn hc-popup-btn--ghost"
    onClick={async () => {
      try {
        await downloadHotelReceipt(
  dbBooking.id,
  token,
  dbBooking.bookingReference || dbBooking.booking_reference
);
      } catch (err) {
        alert(err.message);
      }
    }}
  >
    Download Receipt
  </button>
)}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────
export default function HotelConfirm() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { expireSession, token } = useAuth();

  // Booking comes from navigation state or localStorage fallback
  // (same pattern as BusConfirm)
  const booking   = location.state?.booking ?? readHotelBooking();
  const countdown = useCountdown(15 * 60); // 15 min for hotel

  const [paying,       setPaying]       = useState(false);
  const [payError,     setPayError]     = useState("");
  const [releasing, setReleasing] = useState(false);
  const [showSuccess,  setShowSuccess]  = useState(false);
  const [confirmedTxn, setConfirmedTxn] = useState("");
  const [dbBooking,    setDbBooking]    = useState(null);
  const supplierBookingRef = useRef("");

  const nights = booking ? calcNights(booking.checkIn, booking.checkOut) : 1;
    const quotePricing = booking?.pricing || null;

  const finalAmount = Number(
    quotePricing?.payableTotal ??
    booking?.grandTotal ??
    booking?.totalAmount ??
    booking?.totalPrice ??
    0
  );

  const grandTotal = finalAmount;

  // ── Handle Easebuzz return via URL params (same as bus) ──────────────────
  useEffect(() => {
    const params  = new URLSearchParams(window.location.search);
    const status  = params.get("payment_status") || params.get("status");
    const txnid   = params.get("txnid") || params.get("transaction_id");
    const easeTxn = params.get("easepayid");

    if (status === "success" && txnid) {
      window.history.replaceState({}, "", window.location.pathname);
      const saved = readHotelBooking();
      // Verify server-side (gateway API when the redirect carries no hash),
      // then create the booking from the verified transaction.
      verifyHotelEasebuzzPayment({ txnid }, getStoredToken())
        .catch((err) => console.warn("[HotelConfirm] verify-first deferred:", err.message))
        .then(() => confirmHotelBooking(txnid, easeTxn, saved))
        .then((result) => {
  const normalizedBooking = {
    ...result,
    id: result?.id || result?.booking_id || result?._id,
    bookingReference:
      result?.bookingReference ||
      result?.booking_reference ||
      result?.bookingRefNo ||
      result?.booking_ref_no,
  };

  setDbBooking(normalizedBooking);
        setConfirmedTxn(txnid);
        setShowSuccess(true);
      })
      .catch((err) => {
        setPayError(err.message || "Payment succeeded but booking save failed. Please contact support.");
      });
    } else if (status === "failure" || status === "userCancelled") {
      window.history.replaceState({}, "", window.location.pathname);
      setPayError("Payment was not completed. Please try again.");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmHotelBooking = useCallback(async (txnid, easepayid, bookingData) => {
    const saved = bookingData || readHotelBooking();
    if (!saved) throw new Error("Booking data missing.");
    if (!saved.requestId) throw new Error("Hotel booking requestId is missing. Please restart the hotel search.");
    if (supplierBookingRef.current) return supplierBookingRef.current;

    

const supplier = await hotelApi.book({
  ...saved,
  onHold: false,
  paymentMethod: "easebuzz",
  paymentStatus: "success",

  // Reuse the same server-generated quote used for payment.
  quoteId: saved.quoteId || "",
  pricing: saved.pricing || null,

  // Never fall back to an unverified client-side total when a quote exists.
  totalAmount: Number(
    saved.pricing?.payableTotal ??
    saved.grandTotal ??
    saved.totalAmount ??
    saved.totalPrice ??
    0
  ),

  guestContactNumber: saved.guestContactNumber || saved.guestPhone,
  guestEmail: saved.guestEmail,
  txnid,
  easepayid,
});

    const bookingReference =
      supplier?.bookingReference ||
      supplier?.bookingRefNo ||
      supplier?.supplier?.bookingRefNo ||
      "";

    if (!bookingReference) {
      throw new Error("Supplier did not return a hotel booking reference.");
    }

    const confirmed = {
      ...saved,
      ...supplier,
      bookingReference,
      bookingStatus: supplier?.bookingStatus || supplier?.supplier?.bookingStatus || null,
      confirmationNumber: supplier?.confirmationNumber || supplier?.supplier?.confirmationNumber || null,
      holdExpiryAt: supplier?.holdExpiryAt || supplier?.supplier?.holdExpiryAt || null,
      txnid,
      easepayid,
      paymentStatus: supplier?.paymentStatus || "success",
      paymentMethod: "easebuzz",
    };

    supplierBookingRef.current = confirmed;
    saveHotelBooking(confirmed);
    return confirmed;
  }, []);

  // ── Back button: fall back to hotels page if no browser history ─────────
  const handleBack = useCallback(() => {
    if (paying) return; // safety guard, though button is already disabled

    // window.history.state.idx > 0 means there IS a previous entry in this SPA session
    const canGoBack = window.history.state && window.history.state.idx > 0;

    if (canGoBack) {
      navigate(-1);
    } else {
      navigate("/hotels");
    }
  }, [navigate, paying]);

  // ── Payment initiation (mirrors bus handlePay exactly) ───────────────────
  const handlePay = async () => {
    if (!booking) { setPayError("Booking data missing — please go back and try again."); return; }
    if (countdown.expired) { setPayError("Session expired. Please go back and start again."); return; }
    if (!Number.isFinite(finalAmount) || finalAmount <= 0) {
      setPayError("A valid hotel booking amount is required.");
      return;
    }
    if (!booking.requestId) {
      setPayError("Hotel booking requestId is missing. Please restart the hotel search.");
      return;
    }

    setPaying(true);
    setPayError("");

    try {
      const token = getStoredToken();
      if (!token) throw new Error("Please log in to continue payment.");

      const guestName  = booking.guestFirstName || booking.guest?.firstName || "Traveller";
      const guestEmail = booking.guestEmail     || booking.guest?.email     || "";
      const guestPhone = booking.guestPhone     || booking.guest?.phone     || "";

      if (!guestEmail) throw new Error("Guest email is required for payment. Please go back and fill in guest details.");
      if (!guestPhone) throw new Error("Guest phone is required for payment. Please go back and fill in guest details.");

      const initData = await initiateHotelEasebuzzPayment({
        booking,
        amount: finalAmount,
        nights,
        token,
        onAuthExpired: expireSession,
      });

      // ✅ Save to localStorage before leaving page (same as bus)
      saveHotelBooking({
        ...booking,
        paymentInitiated: true,
        txnid: initData.txnid,
      });

      // ── Step 2: Load and launch Easebuzz (identical to bus) ──────────────
      const EasebuzzCheckout = await loadEasebuzzCheckout();
      const easebuzzCheckout = new EasebuzzCheckout(initData.key, initData.env || "test");

      easebuzzCheckout.initiatePayment({
        access_key: initData.access_key,
        onResponse: async (response) => {
          if (response?.status === "success") {
            try {
              const txnid = response.txnid || initData.txnid;
              // Server-side verification FIRST — the booking is only created
              // after the backend reconciles signature/amount with Easebuzz.
              const verification = await verifyHotelEasebuzzPayment(response, token);
              if (!verification.verified) {
                throw new Error(
                  "Payment could not be verified yet. Please wait a moment and try again."
                );
              }
              const result = await confirmHotelBooking(
  txnid,
  response.easepayid,
  booking
);

const normalizedBooking = {
  ...result,
  id: result?.id || result?.booking_id || result?._id,
  bookingReference:
    result?.bookingReference ||
    result?.booking_reference ||
    result?.bookingRefNo ||
    result?.booking_ref_no,
};

setDbBooking(normalizedBooking);
              setConfirmedTxn(txnid);
              setShowSuccess(true);
            } catch {
              setPayError(
                "Payment succeeded, but booking save failed. Please contact support with your transaction ID: " +
                (response.txnid || initData.txnid)
              );
            } finally {
              setPaying(false);
            }
            return;
          }

          setPayError(`Payment ${response?.status || "failed"}. Please try again.`);
          setPaying(false);
        },
        theme: "#0f766e", // hotel green theme vs bus teal
      });

    } catch (err) {
      console.error("Hotel payment error:", err);
      setPayError(err?.message ?? "Payment failed. Please try again.");
      setPaying(false);
    }
  };

  // ── Empty state ──────────────────────────────────────────────────────────
  if (!booking) {
    return (
      <main className="hc-page hc-empty">
        <AlertCircle size={48} color="#e05252" />
        <h2>No booking found</h2>
        <p>Please select a hotel room and fill in guest details first.</p>
        <button onClick={() => navigate("/hotels")}>Search Hotels</button>
      </main>
    );
  }

  const timerPct = (countdown.remaining / (15 * 60)) * 100;
  const occupancies = Array.isArray(booking.occupancies)
  ? booking.occupancies
  : [];

const totalAdults = occupancies.reduce(
  (sum, room) => sum + Number(room.adultCount || 0),
  0
);

const totalChildren = occupancies.reduce(
  (sum, room) => sum + Number(room.childCount || 0),
  0
);

  const totalRooms =
  Number(booking.rooms) ||
  occupancies.length ||
  1;
  const gstDetails = booking.gstDetails?.enabled ? booking.gstDetails : null;

const selectedRoomSummary = Array.isArray(booking.roomSelections) && booking.roomSelections.length > 0
  ? booking.roomSelections
      .filter((room) => Number(room.qty || 0) > 0)
      .map((room) => {
        const qty = Number(room.qty || 0);
        return `${room.roomName || "Room"}${qty > 1 ? ` × ${qty}` : ""}`;
      })
      .join(" | ")
  : booking.selectedRoom?.name || booking.rateName || "Room";

  return (
    <>
      {showSuccess && (
        <SuccessPopup
          booking={booking}
          txnid={confirmedTxn}
          dbBooking={dbBooking}
          token={token}
          onClose={() => { setShowSuccess(false); navigate("/profile?tab=hotels"); }}
        />
      )}

      <main className="hc-page">

        {/* ── Top Bar ── */}
        <header className="hc-topbar">
          <button
            className="hc-back-btn"
            onClick={handleBack}
            disabled={paying}
          >
            <ArrowLeft size={16} /> Back
          </button>

          <nav className="hc-steps" aria-label="Booking steps">
            <span className="hc-step hc-step--done">
              <span className="hc-step-num">✓</span> Guest Details
            </span>
            <span className="hc-step-arrow">›</span>
            <span className="hc-step hc-step--active">
              <span className="hc-step-num">2</span> Review &amp; Pay
            </span>
          </nav>

          <div className={`hc-timer ${countdown.expired ? "hc-timer--expired" : countdown.remaining < 120 ? "hc-timer--warn" : ""}`}>
            <Clock size={14} />
            {countdown.expired ? "Session expired" : `${countdown.display} left`}
            {!countdown.expired && (
              <span className="hc-timer-bar">
                <span className="hc-timer-fill" style={{ width: `${timerPct}%` }} />
              </span>
            )}
          </div>
        </header>

        {/* ── Layout ── */}
        <div className="hc-layout">

          {/* ── LEFT ── */}
          <div className="hc-main">

            {/* Hotel Summary */}
            <section className="hc-card" aria-label="Hotel summary">
              <div className="hc-card-head">
                <BedDouble size={16} /> Hotel Summary
              </div>

              <div className="hc-hotel-hero">
                {booking.image && (
                  <img
                    src={booking.image}
                    alt={booking.name}
                    className="hc-hotel-img"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                )}
                <div className="hc-hotel-info">
                  <h2 className="hc-hotel-name">{booking.name || booking.hotelName}</h2>
                  {booking.stars > 0 && (
                    <div className="hc-stars">
                      {Array.from({ length: booking.stars }).map((_, i) => (
                        <Star key={i} size={13} fill="#f59e0b" color="#f59e0b" />
                      ))}
                    </div>
                  )}
                  <div className="hc-hotel-location">
                    <MapPin size={13} />
                    {booking.location || booking.locationDetail || "—"}
                  </div>
                </div>
              </div>

              <div className="hc-stay-grid">
                <div className="hc-stay-cell">
                  <span className="hc-stay-label">Check-in</span>
                  <strong>{formatDate(booking.checkIn)}</strong>
                  {booking.checkInTime && <small>{booking.checkInTime}</small>}
                </div>
                <div className="hc-stay-divider">
                  <span className="hc-nights-badge">{nights}N</span>
                </div>
                <div className="hc-stay-cell">
                  <span className="hc-stay-label">Check-out</span>
                  <strong>{formatDate(booking.checkOut)}</strong>
                  {booking.checkOutTime && <small>{booking.checkOutTime}</small>}
                </div>
              </div>

              <div className="hc-meta-chips">
                <div className="hc-meta-chip">
                  <BedDouble size={13} />
                  <span>{selectedRoomSummary}</span>
                </div>
                <div className="hc-meta-chip">
  <BedDouble size={13} />
  <span>
    {totalRooms} Room{totalRooms !== 1 ? "s" : ""}
  </span>
</div>

<div className="hc-meta-chip">
  <Users size={13} />
  <span>
    {totalAdults} Adult{totalAdults !== 1 ? "s" : ""}
    {totalChildren > 0 &&
      `, ${totalChildren} Child${totalChildren !== 1 ? "ren" : ""}`}
  </span>
</div>
                {booking.refundable !== undefined && (
                  <div className={`hc-meta-chip ${booking.refundable ? "hc-chip--green" : "hc-chip--orange"}`}>
                    <CheckCircle2 size={13} />
                    <span>{booking.refundable ? "Free Cancellation" : "Non-refundable"}</span>
                  </div>
                )}
              </div>
            </section>

            {/* Guest Details */}
<section className="hc-card hc-traveller-card" aria-label="Guest details">
  <div className="hc-traveller-head">
    <div>
      <div className="hc-traveller-title">
        <span className="hc-traveller-title-icon">
          <Users size={17} />
        </span>

        <span>Traveller Details</span>
      </div>

      <p>Review guest information before payment</p>
    </div>

    <div className="hc-traveller-count">
      {Array.isArray(booking.guests) ? booking.guests.length : 1}
      <span>Guests</span>
    </div>
  </div>

  <div className="hc-traveller-grid">
    {Array.isArray(booking.guests) && booking.guests.length > 0 ? (
      booking.guests.map((guest, index) => {
        const isChild =
          String(guest.guestType || "").toUpperCase() === "CHILD";

        const sameTypeBefore = booking.guests
          .slice(0, index)
          .filter(
            (item) =>
              String(item.guestType || "").toUpperCase() ===
              String(guest.guestType || "").toUpperCase()
          ).length;

        const guestLabel = isChild
          ? `Child ${sameTypeBefore + 1}`
          : `Adult ${sameTypeBefore + 1}`;

        return (
          <div
            className={`hc-traveller-item ${
              isChild ? "hc-traveller-item--child" : ""
            }`}
            key={index}
          >
            <div className="hc-traveller-avatar">
              {(guest.firstName?.[0] || "G").toUpperCase()}
            </div>

            <div className="hc-traveller-details">
              <div className="hc-traveller-top">
                <span
                  className={`hc-traveller-type ${
                    isChild ? "hc-traveller-type--child" : ""
                  }`}
                >
                  {guestLabel}
                </span>

                {index === 0 && !isChild && (
                  <span className="hc-primary-guest">
                    Primary Guest
                  </span>
                )}
              </div>

              <strong className="hc-traveller-name">
                {guest.firstName || ""} {guest.lastName || ""}
              </strong>

              {isChild && guest.age !== undefined && (
                <span className="hc-traveller-age">
                  Age {guest.age} years
                </span>
              )}
            </div>

            <div className="hc-traveller-check">
              <CheckCircle2 size={17} />
            </div>
          </div>
        );
      })
    ) : (
      <div className="hc-traveller-item">
        <div className="hc-traveller-avatar">
          {(booking.guestFirstName?.[0] || "G").toUpperCase()}
        </div>

        <div className="hc-traveller-details">
          <span className="hc-traveller-type">
            Primary Guest
          </span>

          <strong className="hc-traveller-name">
            {booking.guestFirstName || ""}{" "}
            {booking.guestLastName || ""}
          </strong>
        </div>
      </div>
    )}
  </div>

  <div className="hc-contact-card">
    <div className="hc-contact-copy">
      <span className="hc-contact-label">
        Booking contact
      </span>

      <strong>{booking.guestEmail || "—"}</strong>

      <span>
        +91 {booking.guestPhone || "—"}
      </span>
    </div>

    <ShieldCheck size={19} />
  </div>

  {booking.specialRequestsText && (
    <div className="hc-request-card">
      <span>Special Requests</span>
      <strong>{booking.specialRequestsText}</strong>
    </div>
  )}

  {gstDetails && (
    <div className="hc-request-card">
      <span>GST Details</span>
      <strong>{gstDetails.gstin}</strong>
      <small>{gstDetails.companyName}</small>
      <small>{gstDetails.address}</small>
    </div>
  )}
</section>

            {/* Payment Section */}
            <section className="hc-card hc-payment-card" aria-label="Payment">
              <div className="hc-card-head hc-card-head--spaced">
                <span><CreditCard size={16} /> Complete Payment</span>
                <span className={`hc-timer-inline ${countdown.expired ? "hc-timer--expired" : countdown.remaining < 120 ? "hc-timer--warn" : ""}`}>
                  <Clock size={12} /> {countdown.expired ? "Expired" : countdown.display}
                </span>
              </div>

              <div className="hc-pay-body">
                <p className="hc-pay-info">
                  You'll be redirected to <strong>Easebuzz</strong> secure checkout.
                  All major payment methods accepted.
                </p>

                <div className="hc-pay-methods">
                  <div className="hc-pm-pill"><CreditCard size={14} /> Cards</div>
                  <div className="hc-pm-pill"><Smartphone size={14} /> UPI</div>
                  <div className="hc-pm-pill"><Landmark size={14} /> Net Banking</div>
                  <div className="hc-pm-pill"><Wallet size={14} /> Wallets</div>
                </div>

                {payError && (
                  <div className="hc-error-banner" role="alert">
                    <AlertCircle size={15} /> {payError}
                  </div>
                )}

                <button
                  className="hc-pay-btn"
                  disabled={paying || countdown.expired}
                  onClick={handlePay}
                >
                  {paying
                    ? <><span className="hc-spinner" /> Preparing secure checkout…</>
                    : countdown.expired
                      ? "Session Expired — Go Back"
                      : `Pay ${money(grandTotal)} securely →`}
                </button>

                <p className="hc-secure-note">
                  <ShieldCheck size={13} /> 256-bit SSL encrypted · PCI-DSS compliant · 100% secure
                </p>
              </div>
            </section>
          </div>

          {/* ── RIGHT: Price Summary ── */}
          <aside className="hc-price-card">
            <div className="hc-card-head">Price Summary</div>

            <div className="hc-price-rows">
              {quotePricing?.breakdown ? (
                <>
                  <div className="hc-price-row">
                    <span>Room total · {nights}N · {totalRooms}R</span>
                    <strong>{money(quotePricing.supplier?.roomTotal ?? quotePricing.supplierRoomTotal ?? 0)}</strong>
                  </div>
                  {Number(quotePricing.supplier?.addOnTotal ?? quotePricing.addOnTotal) > 0 && (
                    <div className="hc-price-row">
                      <span>Meal Add-ons</span>
                      <strong>{money(quotePricing.supplier?.addOnTotal ?? quotePricing.addOnTotal)}</strong>
                    </div>
                  )}
                  {quotePricing.breakdown
                    .filter((line) => Number(line.amount) > 0)
                    .map((line) => (
                      <div className="hc-price-row" key={line.code}>
                        <span>
                          {line.label}
                          {line.chargeType === "percent" && Number(line.value) > 0
                            ? ` @${Number(line.value)}%`
                            : ""}
                        </span>
                        <strong>{money(line.amount)}</strong>
                      </div>
                    ))}
                  <div className="hc-price-row hc-price-row--total">
                    <span>Grand Total</span>
                    <strong>{money(grandTotal)}</strong>
                  </div>
                </>
              ) : (
                <>
                  <div className="hc-price-row">
                    <span>Room total · {nights}N · {totalRooms}R</span>
                    <strong>{money(booking.grandTotal || booking.totalAmount || booking.totalPrice)}</strong>
                  </div>
                  {Number(booking.taxes) > 0 && (
                    <div className="hc-price-row">
                      <span>Taxes &amp; Fees</span>
                      <strong>{money(booking.taxes)}</strong>
                    </div>
                  )}
                  <div className="hc-price-row hc-price-row--total">
                    <span>Grand Total</span>
                    <strong>{money(grandTotal)}</strong>
                  </div>
                </>
              )}
            </div>

            <div className="hc-price-note">
              <ShieldCheck size={13} />{" "}
              {quotePricing
                ? `Server-confirmed price (config v${quotePricing.settingsVersion ?? quotePricing.settingsversion ?? "—"})`
                : "All taxes included"}
            </div>

            <div className="hc-secure-badge">
              <ShieldCheck size={14} /> 100% Secure Payment
            </div>

            {booking.cancellationPolicies?.length > 0 && (
              <div className="hc-cancellation-note">
                <AlertCircle size={13} />
                <span>
                  {booking.cancellationPolicies[0].text
                    ?.replace(/<[^>]*>/g, "").trim()
                    .slice(0, 120) || "Cancellation policy applies."}
                </span>
              </div>
            )}
          </aside>
        </div>
      </main>
    </>
  );
}
